/* ErrorBoundary — React error boundary with a design-system fallback */

import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'
import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/Button'
import {
  attemptStaleChunkRecovery,
  isStaleChunkError,
  reloadCurrentPage,
} from '@/lib/chunk-recovery'

interface Props {
  children: ReactNode
  /** Replace the default fallback entirely. */
  fallback?: ReactNode
  /** Where this boundary sits, e.g. 'route' — included in the console record. */
  label?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Sentry reporting is NOT wired here on purpose.
 *
 * `main.tsx` passes `onCaughtError`/`onUncaughtError` to `createRoot`, both bound to
 * `Sentry.reactErrorHandler()`. React 19 routes an error that a boundary catches to
 * `onCaughtError`, so mounting this boundary moves reporting from the uncaught hook to the
 * caught one — it does not lose it. Reporting again here would double-count every error.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept for local debugging; Sentry gets this via createRoot's onCaughtError.
    console.error(`[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ''}]`, error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (!this.state.hasError) return this.props.children
    if (this.props.fallback) return this.props.fallback
    return <ErrorFallback error={this.state.error} onRetry={this.handleReset} />
  }
}

/**
 * The shared fallback, used by both this boundary and the router's `defaultErrorComponent`
 * (`main.tsx`) so a route error and a provider error present identically.
 *
 * Copy is deliberate. "Something went wrong" tells the reader nothing and apologises instead of
 * directing. This names what failed, what is still usable, and what to do — which matters more
 * here than in most products, because a governance user who hits an error mid-filing needs to
 * know whether their records were affected.
 */
interface ErrorFallbackProps {
  error: Error | null
  onRetry: () => void
  /** Test seams; production uses the guarded recovery functions above. */
  recoverStaleChunk?: (error: unknown) => boolean
  reloadPage?: () => void
}

export function ErrorFallback({
  error,
  onRetry,
  recoverStaleChunk = attemptStaleChunkRecovery,
  reloadPage = reloadCurrentPage,
}: ErrorFallbackProps) {
  const staleChunk = isStaleChunkError(error)

  useEffect(() => {
    if (staleChunk) recoverStaleChunk(error)
  }, [error, recoverStaleChunk, staleChunk])

  return (
    <div role="alert" className="flex flex-col items-center justify-center py-16 px-6">
      <div className="w-10 h-10 rounded-lg bg-danger-muted flex items-center justify-center mb-3">
        <AlertCircle size={20} className="text-danger" />
      </div>

      <Text variant="title-md" as="h2" className="mb-1">
        This page didn&rsquo;t load
      </Text>
      <Text variant="body-sm" color="muted" className="text-center max-w-md mb-4">
        {staleChunk
          ? 'The app was updated while this page was open. Reload to use the latest version.'
          : 'Nothing was saved or changed. The rest of the app is still working — try again, or move on and come back to this page.'}
      </Text>

      <Button onClick={staleChunk ? reloadPage : onRetry}>
        {staleChunk ? 'Reload app' : 'Try again'}
      </Button>

      {error?.message && (
        <Text variant="label-sm" color="subtle" mono className="mt-4 text-center max-w-md break-words">
          {error.message}
        </Text>
      )}
    </div>
  )
}
